import * as Sentry from '@sentry/react-native';
import { useRouter, usePathname, useLocalSearchParams } from 'expo-router';
import { usePostHog } from 'posthog-js/react';
import { useEffect, useRef } from 'react';
import logger from '../utils/logger';

/**
 * Hook to detect and handle OAuth errors from URL parameters.
 *
 * OAuth errors arrive in two shapes from Supabase:
 * - Query params: ?error=invalid_request&error_code=bad_oauth_state&error_description=...
 * - Hash params: #error=access_denied&error_description=...
 *
 * Mounted globally from app/_layout.tsx so it runs on every route — Supabase's
 * redirectTo can land on `/`, `/onboarding`, or `/auth-callback`, and we need
 * to handle the error on whichever one Supabase chose. Dedupes by (pathname +
 * error params) so the same error is only processed once per URL, but the next
 * OAuth attempt with a different code will still be handled.
 *
 * When an error is detected:
 * 1. Logs to PostHog and Sentry (warning level)
 * 2. If not already on /onboarding, redirects there with ?oauth_error=<code>
 * 3. If on /onboarding, replaces the raw error params with ?oauth_error=<code>
 *    in place so OnboardingScreen can render the user-facing message.
 */
export const useOAuthErrorHandler = () => {
  const router = useRouter();
  const posthog = usePostHog();
  const pathname = usePathname();
  const params = useLocalSearchParams();
  const processedKeyRef = useRef(null);

  useEffect(() => {
    if (typeof window === 'undefined' || !window.location) return;

    const searchParams = new URLSearchParams(window.location.search || '');
    const hashParams = new URLSearchParams((window.location.hash || '').replace(/^#/, ''));

    const error = searchParams.get('error') || hashParams.get('error');
    const errorCode = searchParams.get('error_code') || hashParams.get('error_code');
    const errorDescription = searchParams.get('error_description') || hashParams.get('error_description');

    if (!error) return;

    // Dedupe per (pathname + error identifier) so we don't reprocess on every
    // render but DO process a fresh OAuth attempt that yields a different error.
    const errorKey = `${pathname}|${errorCode || error}`;
    if (processedKeyRef.current === errorKey) return;
    processedKeyRef.current = errorKey;

    const errorData = {
      error,
      error_code: errorCode,
      error_description: errorDescription,
      pathname,
      url: window.location.href,
      timestamp: new Date().toISOString(),
    };

    logger.error('[useOAuthErrorHandler] OAuth error detected:', errorData);

    // Telemetry first, redirect second. posthog-js buffers pre-init captures;
    // Sentry does not, so _layout.tsx skips its init defer when error params
    // are present on the URL (see initSentry + initPostHog guards).
    try {
      posthog?.capture('oauth_error_detected', errorData);
    } catch (e) {
      logger.warn('[useOAuthErrorHandler] posthog capture failed:', e);
    }

    try {
      Sentry.captureMessage(`OAuth Error: ${errorCode || error}`, {
        level: 'warning',
        tags: {
          error_type: 'oauth',
          error_code: errorCode || error,
          pathname,
        },
        contexts: { oauth: errorData },
      });
    } catch (e) {
      logger.warn('[useOAuthErrorHandler] sentry capture failed:', e);
    }

    const errorParam = errorCode || error;

    if (pathname === '/onboarding') {
      // Already on the screen that renders the error banner — just swap the
      // raw Supabase params for the normalised oauth_error param in place so
      // OnboardingScreen picks it up.
      router.replace(`/onboarding?oauth_error=${encodeURIComponent(errorParam)}`);
    } else {
      logger.log(`[useOAuthErrorHandler] Redirecting to /onboarding with error: ${errorParam}`);
      router.replace(`/onboarding?oauth_error=${encodeURIComponent(errorParam)}`);
    }
  }, [router, posthog, pathname, params]);
};
